import type { QuizDto } from '@/api/webs'

// Pure pieces of the inbound-webhook docs' quiz section (ORB-915), kept out of the component file so
// fast-refresh stays happy and they can be tested directly.

const QUESTION_PLACEHOLDER = '<QUESTION_ID>'

// Jane's answers from the Verto example; any whole numbers on the scale would do.
const SAMPLE_VALUES = [7, 5, 3, 6, 4, 2, 6, 5, 8]

function sampleValue(index: number, scaleMax: number): number {
  const value = SAMPLE_VALUES[index % SAMPLE_VALUES.length]
  return scaleMax >= 10 ? value : Math.max(1, Math.ceil(value / 2))
}

/**
 * The "quiz" lines of the curl example, ending in a comma so customFields follows. A web's own example uses its
 * question IDs; the general one uses a placeholder with a number and a null; a web without a quiz sends none.
 */
export function quizBlockExample(quiz: QuizDto | null | 'placeholder'): string {
  if (quiz === null) return ''
  const entries = quiz === 'placeholder'
    ? [`{ "id": "${QUESTION_PLACEHOLDER}", "value": 7 }`, `{ "id": "${QUESTION_PLACEHOLDER}", "value": null }`]
    : alignedEntries(quiz)
  return [
    '    "quiz": {',
    '      "answers": [',
    entries.map(entry => `        ${entry}`).join(',\n'),
    '      ]',
    '    },',
  ].join('\n') + '\n'
}

function alignedEntries(quiz: QuizDto): string[] {
  const width = Math.max(0, ...quiz.Questions.map(question => question.Id.length))
  return quiz.Questions.map((question, index) => {
    const id = `"${question.Id}",`.padEnd(width + 3)
    return `{ "id": ${id} "value": ${sampleValue(index, quiz.ScaleMax)} }`
  })
}

export interface SentencePart { text: string; code?: boolean }

/** "null means Not applicable, and only a allows it." worded for none, one or several questions. */
export function notApplicableSentence(questionIds: string[]): SentencePart[] {
  if (questionIds.length === 0) {
    return [{ text: 'No question on this quiz allows Not applicable, so every value is a whole number.' }]
  }
  const parts: SentencePart[] = [{ text: 'null', code: true }, { text: ' means Not applicable, and only ' }]
  questionIds.forEach((id, index) => {
    if (index > 0) parts.push({ text: index === questionIds.length - 1 ? ' and ' : ', ' })
    parts.push({ text: id, code: true })
  })
  parts.push({ text: questionIds.length === 1 ? ' allows it.' : ' allow it.' })
  return parts
}

export function quizPlacementSentence(detailsPlacement: string): string {
  return detailsPlacement === 'Before'
    ? 'This quiz asks for details before the questions. Make a UUID for the attempt and post the details with quiz.attemptId, leaving out quiz.answers. The 202 response returns QuizAttemptId. Keep that ID and send it as quiz.attemptId with the same details and all answers when finished. Both posts update one attempt, and no report or email is created until the answers arrive. Reuse the ID when retrying; use a new ID for a retake. Name and email alone do not identify an attempt.'
    : 'This quiz asks for details after the questions, so one post carries everything.'
}
